// @givmo/employer-module — the fields that choose what a person can open (EP-SUITE8): the approve
// screen's Global Admin card and app list (`AppChoiceFields`), and the parts the portal's Edit admin
// composes in the console's approved demotion design (`GlobalAdminCard`, `GlobalAdminNotice`,
// `AppChoiceList`; D-EP-76 (3)). Drawn in the console's own pattern for this decision — its invite
// composer and access editor (`@givmo/org-console`): a switch per app, its level once it is on — so
// the same choice reads the same wherever it is made.
//
// It renders a draft and reports edits; the rules (nothing pre-checked, every app on the list, the
// body) are `app-choice.ts`'s, and the words `app-choice-words.ts`'s.
//
// ⚠ AN APP THAT IS OFF IS LISTED AND CHOOSABLE, marked "Off for the company" (D-EP-76 (2), the
// console's rule): the server takes it, and the grant waits for it to be turned on.
//
// DATA CONTRACT: `<idPrefix>-global-admin` (the switch), `-global-admin-notice`, `-apps`,
// `-app-<slug>` (the row), `-app-<slug>-off` (the mark on an app that is off), `-app-<slug>-switch`,
// `-app-<slug>-tier` (the level, shown once the app is on), `-apps-loading`, `-apps-empty`,
// `-apps-error` and `-apps-retry`.

import type { ReactNode } from 'react';
import { Badge, Button, Select, Switch, testIdProp } from '@givmo/design-system';
import { productLabel } from '../../lib/app-names';
import { withApp, type AppChoiceDraft, type AppDraft, type AppTier } from './app-choice';
import { APP_CHOICE_WORDS } from './app-choice-words';
import { ROLE_MEMBER, ROLE_OWNER } from './admins-roles';

export type AppListStatus = 'loading' | 'error' | 'ready';

const QUIET = 'font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-tertiary';
const STRONG = 'font-sans text-s14 font-w600 tracking-givmo text-text-strong';

export interface GlobalAdminCardProps {
  idPrefix: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  /** Held while the act is in flight. */
  disabled?: boolean;
}

/** The whole-company role as a switch: the Global Admin card of the console's composer and editor. */
export function GlobalAdminCard({
  idPrefix,
  checked,
  onCheckedChange,
  disabled = false,
}: GlobalAdminCardProps) {
  return (
    <div className="flex items-start justify-between gap-16 rounded-lg border border-border-subtle p-16">
      <div className="min-w-0">
        <p className={STRONG}>{APP_CHOICE_WORDS.globalAdmin}</p>
        <p className={`mt-2 ${QUIET}`}>{APP_CHOICE_WORDS.globalAdminDescription}</p>
      </div>
      <Switch
        appearance="saas"
        checked={checked}
        disabled={disabled}
        onCheckedChange={onCheckedChange}
        aria-label={APP_CHOICE_WORDS.globalAdmin}
        testId={`${idPrefix}-global-admin`}
      />
    </div>
  );
}

/** What a Global Admin holds, in the app list's place while the switch is on. */
export function GlobalAdminNotice({ idPrefix }: { idPrefix: string }) {
  return (
    <p
      className="rounded-lg bg-surface-sunken p-16 font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary"
      {...testIdProp(`${idPrefix}-global-admin-notice`)}
    >
      {APP_CHOICE_WORDS.globalAdminNotice}
    </p>
  );
}

export interface AppChoiceListProps {
  idPrefix: string;
  draft: AppChoiceDraft;
  onDraftChange: (next: AppChoiceDraft) => void;
  /** The `module-choices` read behind the list. */
  status: AppListStatus;
  onRetry: () => void;
  /** What the list opens with: the approve screen's heading, or the demotion's line. */
  lead: ReactNode;
  /** Held while the act is in flight. */
  disabled?: boolean;
}

/** Every app on the company's list, each with whether they get it and at what level. */
export function AppChoiceList({
  idPrefix,
  draft,
  onDraftChange,
  status,
  onRetry,
  lead,
  disabled = false,
}: AppChoiceListProps) {
  return (
    <div className="flex flex-col gap-12" {...testIdProp(`${idPrefix}-apps`)}>
      {lead}
      {status === 'ready' &&
        draft.apps.map((app) => (
          <AppRow
            key={app.module}
            idPrefix={`${idPrefix}-app-${app.module}`}
            app={app}
            disabled={disabled}
            onChange={(next) => onDraftChange(withApp(draft, app.module, next))}
          />
        ))}
      {status === 'ready' && draft.apps.length === 0 && (
        <p className={QUIET} {...testIdProp(`${idPrefix}-apps-empty`)}>
          {APP_CHOICE_WORDS.empty}
        </p>
      )}
      {status === 'loading' && (
        <p className={QUIET} {...testIdProp(`${idPrefix}-apps-loading`)}>
          {APP_CHOICE_WORDS.loading}
        </p>
      )}
      {status === 'error' && (
        <div className="flex items-center justify-between gap-12">
          <p className={QUIET} {...testIdProp(`${idPrefix}-apps-error`)}>
            {APP_CHOICE_WORDS.loadFailed}
          </p>
          <Button
            appearance="saas"
            tone="secondary"
            size="sm"
            onClick={onRetry}
            testId={`${idPrefix}-apps-retry`}
          >
            {APP_CHOICE_WORDS.retry}
          </Button>
        </div>
      )}
    </div>
  );
}

export interface AppChoiceFieldsProps {
  idPrefix: string;
  draft: AppChoiceDraft;
  onDraftChange: (next: AppChoiceDraft) => void;
  /** The `module-choices` read behind the list. */
  status: AppListStatus;
  onRetry: () => void;
  /** Held while the act is in flight. */
  disabled?: boolean;
}

/** The approve screen's choice: make them a Global Admin, or exactly the apps chosen. */
export function AppChoiceFields({
  idPrefix,
  draft,
  onDraftChange,
  status,
  onRetry,
  disabled = false,
}: AppChoiceFieldsProps) {
  return (
    <div className="flex flex-col gap-16">
      <GlobalAdminCard
        idPrefix={idPrefix}
        checked={draft.globalAdmin}
        disabled={disabled}
        onCheckedChange={(checked) => onDraftChange({ ...draft, globalAdmin: checked })}
      />
      {draft.globalAdmin ? (
        <GlobalAdminNotice idPrefix={idPrefix} />
      ) : (
        <AppChoiceList
          idPrefix={idPrefix}
          draft={draft}
          onDraftChange={onDraftChange}
          status={status}
          onRetry={onRetry}
          disabled={disabled}
          lead={
            <div>
              <p className={STRONG}>{APP_CHOICE_WORDS.appsHeading}</p>
              <p className={`mt-2 ${QUIET}`}>{APP_CHOICE_WORDS.exactly}</p>
            </div>
          }
        />
      )}
    </div>
  );
}

interface AppRowProps {
  idPrefix: string;
  app: AppDraft;
  disabled: boolean;
  onChange: (next: Partial<Pick<AppDraft, 'chosen' | 'tier'>>) => void;
}

/**
 * One app: whether they get it, and at what level. The level appears only once the app is on — a
 * level for something they will not hold is a control with no subject (the composer's rule). An app
 * the company has switched off says so beside its name and keeps every control live, as the
 * console's editor does: the off switch is the company's, not a statement about this person.
 */
function AppRow({ idPrefix, app, disabled, onChange }: AppRowProps) {
  const label = productLabel(app.module);
  return (
    <section className="rounded-lg border border-border-subtle p-16" {...testIdProp(idPrefix)}>
      <div className="flex items-center justify-between gap-16">
        <div className="flex min-w-0 flex-wrap items-center gap-8">
          <p className={STRONG}>{label}</p>
          {!app.on && (
            <span {...testIdProp(`${idPrefix}-off`)}>
              <Badge tone="neutral">{APP_CHOICE_WORDS.offForCompany}</Badge>
            </span>
          )}
        </div>
        <Switch
          appearance="saas"
          checked={app.chosen}
          disabled={disabled}
          onCheckedChange={(checked) => onChange({ chosen: checked })}
          aria-label={label}
          testId={`${idPrefix}-switch`}
        />
      </div>
      {app.chosen && (
        <div className="mt-16 border-t border-border-subtle pt-16">
          <Select
            appearance="saas"
            label={APP_CHOICE_WORDS.level}
            value={app.tier}
            disabled={disabled}
            onValueChange={(value) => onChange({ tier: toTier(value) })}
            options={app.tiers.map((tier) => ({ value: tier, label: tier }))}
            testId={`${idPrefix}-tier`}
          />
        </div>
      )}
    </section>
  );
}

function toTier(value: string | null): AppTier {
  return value === ROLE_OWNER ? ROLE_OWNER : ROLE_MEMBER;
}
